import { differenceBy, find, toLower, trim } from "lodash/fp";

import { getNewTagColor } from "@app/analytics/components/Tag/utils";
import { getUniqueKey } from "@app/analytics/utils";
import { Tag } from "@app/common/types";

export const TEMP_ID_PREFIX = "temp-id";

export const getTemporaryTagId = () => `${TEMP_ID_PREFIX}-${getUniqueKey()}`;
export const isNewTag = (tag: Tag) => tag.id.includes(TEMP_ID_PREFIX);

const isAlreadyApplied = (tag: Tag, appliedTags: Tag[]) =>
  !isNewTag(tag) && Boolean(find({ id: tag.id }, appliedTags));

export const isNewSuggestedTag = (tag: Tag, appliedTags: Tag[]) =>
  isNewTag(tag) && !isAlreadyApplied(tag, appliedTags);

export const getSuggestedTags = (
  inputValue: string,
  allTags: Tag[],
  suggestedTags: Tag[],
  appliedTags: Tag[]
): Tag[] => {
  if (!inputValue) return [];

  let hasExactMatch = false;
  const caseInsensitiveInputValue = trim(toLower(inputValue));
  const tagSuggestions = allTags.filter((tag) => {
    const caseInsensitiveTagName = toLower(tag.name);
    const tagMatchesExactly =
      caseInsensitiveTagName === caseInsensitiveInputValue;

    if (tagMatchesExactly) {
      // while we're looping through the tags, detect if any of them are an exact match
      hasExactMatch = true;
      return !isAlreadyApplied(tag, appliedTags);
    }
    return (
      !isAlreadyApplied(tag, appliedTags) &&
      caseInsensitiveTagName.includes(caseInsensitiveInputValue)
    );
  });

  // only suggest creating a new tag if a tag with this name doesn't already exist
  if (!hasExactMatch) {
    const existingNewSuggestedTag = find(
      (t) => isNewSuggestedTag(t, appliedTags),
      suggestedTags
    );

    const newTag: Tag = {
      id: existingNewSuggestedTag?.id ?? getTemporaryTagId(),
      name: inputValue,
      color:
        existingNewSuggestedTag?.color ?? getNewTagColor(inputValue, allTags),
      kind: "custom",
      isHidden: false,
    };
    tagSuggestions.push(newTag);
  }

  return differenceBy("name", tagSuggestions, appliedTags);
};
